import { FC } from 'react';
import { Typography } from 'antd';
import { TbInfoCircle } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ellipsisStyling } from '@/utilities';

import SmallTooltip from '../../../tooltips/SmallTooltip';
import { FinancialOverviewMetricCardGridProps } from '../../types';
import { FinancialMetricSparkline } from '../FinancialMetricSparkline';

export const FinancialOverviewMetricCardGrid: FC<FinancialOverviewMetricCardGridProps> = ({
    columnCount,
    items,
    showDescriptionTooltips = false,
}) => {
    const { token } = useAntDesignTheme();

    return (
        <div
            data-table-of-contents-ignore='true'
            style={{
                display: 'grid',
                gridTemplateColumns: columnCount
                    ? `repeat(${columnCount}, minmax(0, 1fr))`
                    : 'repeat(auto-fit, minmax(10rem, 1fr))',
                gap: 12,
            }}
        >
            {items.map((item) => (
                <div
                    key={item.key}
                    style={{
                        border: `1px solid ${token.colorBorderSecondary}`,
                        borderRadius: token.borderRadius,
                        minWidth: 0,
                        padding: '0.75rem',
                    }}
                    aria-label={item.description ? `${item.label}: ${item.description}` : item.label}
                >
                    <div style={{ alignItems: 'center', display: 'flex', gap: '0.25rem', minWidth: 0 }}>
                        <small style={{ color: token.colorTextSecondary, ...ellipsisStyling }}>{item.label}</small>
                        {showDescriptionTooltips && item.description
                            ? (item.tooltip ?? (
                                  <SmallTooltip placement='bottomRight' title={item.description} showInfo={false}>
                                      <TbInfoCircle
                                          aria-label={`${item.label} definition`}
                                          style={{
                                              color: token.colorTextTertiary,
                                              flexShrink: 0,
                                              verticalAlign: '-0.1rem',
                                          }}
                                      />
                                  </SmallTooltip>
                              ))
                            : null}
                        {item.action ? (
                            <span style={{ display: 'flex', marginLeft: 'auto' }}>{item.action}</span>
                        ) : null}
                    </div>
                    <div
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: '0.75rem',
                            justifyContent: 'space-between',
                            marginTop: '0.375rem',
                            minHeight: '3.25rem',
                            minWidth: 0,
                        }}
                    >
                        <Typography.Title
                            level={5}
                            style={{
                                color: token.colorText,
                                flex: '0 1 auto',
                                margin: 0,
                                minWidth: 0,
                                ...ellipsisStyling,
                            }}
                        >
                            {item.value}
                        </Typography.Title>
                        {item.sparkline?.length ? (
                            <FinancialMetricSparkline
                                label={item.sparklineLabel ?? `${item.label} trend`}
                                metricLabel={item.label}
                                points={item.sparkline}
                                valueFormatter={item.sparklineValueFormatter}
                            />
                        ) : null}
                    </div>
                </div>
            ))}
        </div>
    );
};
